import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Button, Text } from "@/components/ui";
import { Field, Input } from "@/components/ui/field";

/** Matches the server's sixty-second resend cooldown (decisions.md §18). */
const RESEND_COOLDOWN_SEC = 60;

type OtpCodeFormProps = {
  purpose: "login" | "verify_email";
  maskedEmail: string;
  /**
   * Show "remember this device" (login only). Off by default: since the owner's 2026-10-16
   * decision every sign-in asks for the code, and the server remembers devices only when
   * `AUTH_TRUSTED_DEVICES=on` (it then says so in the login answer).
   */
  offerRememberDevice?: boolean;
  /** Rejects with an Error whose message is shown to the user (e.g. "4 attempts left"). */
  onSubmit: (code: string, rememberDevice: boolean) => Promise<void>;
  onResend: () => Promise<void>;
  onCancel?: () => void;
  /** Label for the cancel button; defaults to "Use a different account". */
  cancelLabel?: string;
};

/** The emailed-code step shared by sign-in and signup verification (AUTH-003 / AUTH-008). */
export function OtpCodeForm({
  purpose,
  maskedEmail,
  offerRememberDevice = false,
  onSubmit,
  onResend,
  onCancel,
  cancelLabel,
}: OtpCodeFormProps) {
  const t = useTranslations("auth.otp");
  const [code, setCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(true);
  const showRemember = purpose === "login" && offerRememberDevice;
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SEC);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const digits = code.replace(/[\s-]/g, "");
    if (!/^\d{6}$/.test(digits)) {
      setError(t("invalidFormat"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(digits, showRemember && rememberDevice);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : t("failed"));
      setSubmitting(false);
    }
  }

  async function handleResend() {
    setError(null);
    setResent(false);
    try {
      await onResend();
      setResent(true);
      setCode("");
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : t("failed"));
    }
    setCooldown(RESEND_COOLDOWN_SEC);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="otp-form" noValidate>
      <Text tone="muted">{t("sentTo", { email: maskedEmail })}</Text>
      <Text tone="muted" className="text-sm" data-testid="otp-check-spam">
        {t("checkSpam")}
      </Text>
      <Field label={t("codeLabel")} error={error}>
        <Input
          data-testid="otp-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={7}
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="text-center font-mono text-2xl tracking-[0.5em]"
        />
      </Field>
      {showRemember && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            data-testid="otp-remember"
            checked={rememberDevice}
            onChange={(e) => setRememberDevice(e.target.checked)}
          />
          {t("rememberDevice")}
        </label>
      )}
      <Button
        type="submit"
        variant="accent"
        data-testid="otp-submit"
        disabled={submitting}
        className="w-full"
      >
        {submitting ? t("submitting") : t("submit")}
      </Button>
      <div aria-live="polite" className="flex items-center justify-between gap-3 text-sm">
        <button
          type="button"
          data-testid="otp-resend"
          disabled={cooldown > 0}
          onClick={() => void handleResend()}
          className="text-[var(--accent-ink)] underline underline-offset-4 disabled:text-[var(--text-muted)] disabled:no-underline"
        >
          {cooldown > 0 ? t("resendIn", { seconds: cooldown }) : t("resend")}
        </button>
        {onCancel && (
          <button
            type="button"
            data-testid="otp-cancel"
            onClick={onCancel}
            className="text-[var(--text-muted)] underline underline-offset-4"
          >
            {cancelLabel ?? t("useAnotherAccount")}
          </button>
        )}
      </div>
      {resent && (
        <Text tone="muted" data-testid="otp-resent">
          {t("resent")}
        </Text>
      )}
    </form>
  );
}
